import { useState } from "react";

const D = "#0d1117";
const SURFACE = "#161b22";
const SURFACE2 = "#1e2530";
const BORDER = "#2a3342";
const TEXT = "#e2e8f0";
const MUTED = "#7a8899";
const TEAL = "#2ec4b6";
const GOLD = "#f5a623";

const CHIP_OPTIONS = [
  { label: "Web Design", val: "web design for small business" },
  { label: "Getting a Website", val: "getting a website for my business" },
  { label: "Digital Presence", val: "digital presence for local business" },
  { label: "Cheap / Affordable", val: "cheap website small business Australia" },
  { label: "Mobile Sites", val: "mobile website local business" },
  { label: "Trades", val: "trades business website" },
  { label: "Hospitality", val: "cafe restaurant website" },
];

const STEPS = [
  "Searching where business owners ask about websites…",
  "Finding active communities and forums…",
  "Checking search trends and platform activity…",
  "Scanning for free marketing channels…",
  "Analysing messaging that converts…",
  "Building your campaign brief…",
];

interface MarketingResult {
  whereTheyAre: string;
  platforms: { name: string; why: string; effort: string; tactic: string }[];
  actionPlan: { title: string; detail: string }[];
  messagingIntel: string;
}

export default function AdminMarketing() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  const [activeChips, setActiveChips] = useState<string[]>([
    "web design for small business",
    "getting a website for my business",
  ]);
  const [region, setRegion] = useState("Queensland, Australia");
  const [biztype, setBiztype] = useState("all small businesses");

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<MarketingResult | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  async function handleAuth(e: React.FormEvent) {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError("");
    const res = await fetch("/api/admin/auth", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ password }),
    });
    const data = await res.json();
    setAuthLoading(false);
    if (data.ok) {
      setAuthed(true);
    } else {
      setAuthError("Incorrect password.");
    }
  }

  function toggleChip(val: string) {
    setActiveChips(prev =>
      prev.includes(val) ? prev.filter(c => c !== val) : [...prev, val]
    );
  }

  async function runResearch() {
    if (!activeChips.length) return;
    setLoading(true);
    setResult(null);
    setError("");
    setStep(0);
    setProgress(10);

    const milestones = [10, 25, 40, 58, 72, 88];
    let i = 0;
    const timer = setInterval(() => {
      i++;
      if (i < milestones.length) {
        setProgress(milestones[i]);
        setStep(i);
      } else {
        clearInterval(timer);
      }
    }, 7000);

    try {
      const res = await fetch("/api/admin/marketing", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ password, chips: activeChips, region, biztype }),
      });
      clearInterval(timer);
      setProgress(100);
      setStep(5);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed");
      setResult(data);
    } catch (err: any) {
      clearInterval(timer);
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  function copyBrief() {
    if (!result) return;
    const text = [
      "=== BIZDR MARKETING INTEL BRIEF ===\n",
      "WHERE THEY ARE\n" + result.whereTheyAre,
      "\nTOP PLATFORMS\n" + result.platforms.map(p => `${p.name}\n${p.why}\n→ ${p.tactic}`).join("\n\n"),
      "\nACTION PLAN\n" + result.actionPlan.map((a, i) => `${i + 1}. ${a.title}\n${a.detail}`).join("\n\n"),
      "\nMESSAGING INTEL\n" + result.messagingIntel,
    ].join("\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const s = {
    page: { minHeight: "100vh", background: D, color: TEXT, fontFamily: "'DM Sans', sans-serif", position: "relative" as const, overflowX: "hidden" as const },
    grid: { position: "fixed" as const, inset: 0, backgroundImage: "linear-gradient(rgba(46,196,182,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(46,196,182,0.03) 1px,transparent 1px)", backgroundSize: "40px 40px", pointerEvents: "none" as const, zIndex: 0 },
    wrap: { position: "relative" as const, zIndex: 1, maxWidth: 860, margin: "0 auto", padding: "40px 24px 80px" },
    card: { background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 28, marginBottom: 20 },
    label: { fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase" as const, color: TEAL, marginBottom: 12 },
    input: { width: "100%", background: SURFACE2, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 14px", color: TEXT, fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none" },
  };

  if (!authed) {
    return (
      <div style={s.page}>
        <div style={s.grid} />
        <div style={{ ...s.wrap, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
          <div style={{ ...s.card, width: "100%", maxWidth: 380, marginBottom: 0 }}>
            <div style={s.label}>Admin Access</div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, marginBottom: 6, background: `linear-gradient(90deg,${TEAL},${GOLD})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              BizDr · Marketing Intel
            </h2>
            <p style={{ color: MUTED, fontSize: 14, marginBottom: 24 }}>Enter your admin password to access the research tool.</p>
            <form onSubmit={handleAuth} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={s.input}
                data-testid="input-marketing-password"
              />
              {authError && <p style={{ color: "#f87171", fontSize: 13 }}>{authError}</p>}
              <button
                type="submit"
                disabled={authLoading}
                data-testid="button-marketing-auth"
                style={{ padding: "12px", background: `linear-gradient(135deg,${TEAL},#25a99e)`, border: "none", borderRadius: 10, color: D, fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 15, cursor: authLoading ? "not-allowed" : "pointer", opacity: authLoading ? 0.6 : 1 }}>
                {authLoading ? "Checking…" : "Enter"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.grid} />
      <div style={s.wrap}>

        {/* Header */}
        <header style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 48 }}>
          <div style={{ width: 42, height: 42, background: `linear-gradient(135deg,${TEAL},${GOLD})`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16, color: D, flexShrink: 0, fontFamily: "serif" }}>Bd</div>
          <div>
            <h1 style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: 20, color: TEXT, lineHeight: 1 }}>BizDr · Marketing Intel</h1>
            <p style={{ fontSize: 12, color: MUTED, fontFamily: "'DM Mono',monospace", marginTop: 3 }}>bizdr.app/admin/marketing</p>
          </div>
          <div style={{ marginLeft: "auto", fontFamily: "'DM Mono',monospace", fontSize: 10, padding: "4px 10px", border: `1px solid ${BORDER}`, borderRadius: 20, color: MUTED }}>ADMIN ONLY</div>
        </header>

        {/* Search panel */}
        <div style={s.card}>
          <div style={s.label}>Research Engine</div>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, marginBottom: 6, background: `linear-gradient(90deg,${TEAL},${GOLD})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Where are your clients hiding?
          </h2>
          <p style={{ color: MUTED, fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
            Run a live search to find where small business owners are actively looking for web design help right now. Get a full campaign brief with platforms, tactics, and a free-first action plan.
          </p>

          <p style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>Focus areas (select all that apply)</p>
          <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8, marginBottom: 24 }}>
            {CHIP_OPTIONS.map(c => {
              const on = activeChips.includes(c.val);
              return (
                <button key={c.val} onClick={() => toggleChip(c.val)}
                  style={{ background: on ? "rgba(46,196,182,0.12)" : SURFACE2, border: `1px solid ${on ? TEAL : BORDER}`, borderRadius: 20, padding: "6px 14px", fontSize: 13, cursor: "pointer", color: on ? TEAL : MUTED, transition: "all 0.2s" }}>
                  {c.label}
                </button>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" as const }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ display: "block", fontSize: 11, color: MUTED, marginBottom: 6, fontFamily: "'DM Mono',monospace" }}>TARGET REGION</label>
              <input value={region} onChange={e => setRegion(e.target.value)} style={s.input} data-testid="input-region" />
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ display: "block", fontSize: 11, color: MUTED, marginBottom: 6, fontFamily: "'DM Mono',monospace" }}>BUSINESS TYPE FOCUS</label>
              <select value={biztype} onChange={e => setBiztype(e.target.value)}
                style={{ ...s.input, appearance: "none" as const }} data-testid="select-biztype">
                <option value="all small businesses">All small businesses</option>
                <option value="trades and services">Trades and services</option>
                <option value="cafes and hospitality">Cafes and hospitality</option>
                <option value="health and wellness">Health and wellness</option>
                <option value="retail and product businesses">Retail</option>
              </select>
            </div>
          </div>

          <button onClick={runResearch} disabled={loading || !activeChips.length}
            data-testid="button-run-marketing"
            style={{ width: "100%", padding: 14, background: loading ? "#1e3a38" : `linear-gradient(135deg,${TEAL},#25a99e)`, border: "none", borderRadius: 10, color: loading ? TEAL : D, fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 15, cursor: loading || !activeChips.length ? "not-allowed" : "pointer", opacity: !activeChips.length ? 0.5 : 1, transition: "all 0.2s" }}>
            {loading ? "Researching…" : "Run Marketing Intel Research"}
          </button>
        </div>

        {/* Progress */}
        {loading && (
          <div style={s.card}>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, color: TEAL, marginBottom: 12 }}>{STEPS[step]}</div>
            <div style={{ height: 3, background: SURFACE2, borderRadius: 2, overflow: "hidden", marginBottom: 16 }}>
              <div style={{ height: "100%", background: `linear-gradient(90deg,${TEAL},${GOLD})`, borderRadius: 2, width: `${progress}%`, transition: "width 0.4s ease" }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column" as const, gap: 8 }}>
              {STEPS.map((s_, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: i < step ? TEAL : i === step ? TEXT : MUTED }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: i < step ? TEAL : i === step ? GOLD : BORDER, boxShadow: i === step ? `0 0 6px ${GOLD}` : "none", flexShrink: 0 }} />
                  {s_}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 12, padding: 20, fontSize: 14, color: "#f87171", marginBottom: 20 }}>
            {error}
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <>
            <div style={s.card}>
              <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(46,196,182,0.12)", border: "1px solid rgba(46,196,182,0.3)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>📡</span>
                Where They Are Right Now
              </h3>
              <p style={{ fontSize: 14, lineHeight: 1.75, color: "#c9d4e0", whiteSpace: "pre-wrap" }}>{result.whereTheyAre}</p>
            </div>

            <div style={s.card}>
              <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(46,196,182,0.12)", border: "1px solid rgba(46,196,182,0.3)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>🎯</span>
                Top Platforms to Target
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 }}>
                {result.platforms.map((p, i) => (
                  <div key={i} style={{ background: SURFACE2, border: `1px solid ${BORDER}`, borderRadius: 10, padding: 16 }}>
                    <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 14, fontWeight: 700, marginBottom: 4, color: TEXT }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.5 }}>{p.why}</div>
                    <div style={{ display: "inline-block", marginTop: 8, fontSize: 10, fontFamily: "'DM Mono',monospace", padding: "2px 8px", borderRadius: 10, background: p.effort === "low" ? "rgba(46,196,182,0.15)" : p.effort === "medium" ? "rgba(245,166,35,0.15)" : "rgba(239,68,68,0.1)", color: p.effort === "low" ? TEAL : p.effort === "medium" ? GOLD : "#f87171" }}>
                      {p.effort} effort
                    </div>
                    <div style={{ fontSize: 12, color: "#a0b0c0", marginTop: 10, lineHeight: 1.5 }}>→ {p.tactic}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={s.card}>
              <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(245,166,35,0.12)", border: "1px solid rgba(245,166,35,0.3)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>⚡</span>
                Free-First Action Plan
              </h3>
              <div style={{ display: "flex", flexDirection: "column" as const, gap: 12 }}>
                {result.actionPlan.map((a, i) => (
                  <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <div style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(46,196,182,0.12)", border: "1px solid rgba(46,196,182,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Mono',monospace", fontSize: 11, color: TEAL, flexShrink: 0, marginTop: 2 }}>{i + 1}</div>
                    <div>
                      <strong style={{ display: "block", fontSize: 14, fontWeight: 500, color: TEXT, marginBottom: 3 }}>{a.title}</strong>
                      <span style={{ fontSize: 13, color: MUTED, lineHeight: 1.5 }}>{a.detail}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={s.card}>
              <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(245,166,35,0.12)", border: "1px solid rgba(245,166,35,0.3)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>✍️</span>
                What to Say (Messaging Intel)
              </h3>
              <p style={{ fontSize: 14, lineHeight: 1.75, color: "#c9d4e0", whiteSpace: "pre-wrap" }}>{result.messagingIntel}</p>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap" as const }}>
              <button onClick={copyBrief} data-testid="button-copy-brief"
                style={{ padding: "10px 18px", borderRadius: 8, fontFamily: "'DM Mono',monospace", fontSize: 12, cursor: "pointer", border: "1px solid rgba(245,166,35,0.4)", background: "rgba(245,166,35,0.12)", color: GOLD, letterSpacing: "0.04em" }}>
                {copied ? "Copied!" : "Copy Full Brief"}
              </button>
              <button onClick={() => { setResult(null); setError(""); }} data-testid="button-reset"
                style={{ padding: "10px 18px", borderRadius: 8, fontFamily: "'DM Mono',monospace", fontSize: 12, cursor: "pointer", border: `1px solid ${BORDER}`, background: SURFACE2, color: MUTED, letterSpacing: "0.04em" }}>
                Run New Search
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
